import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { fetchCostApproverStatus } from '~/features/cost-estimate/services/cost-estimate.service';
import { useCostApprovalSettings } from '~/features/platform/hooks/use-cost-approval-settings';
import { useMyPreferences } from '~/features/settings/hooks/use-my-preferences';
import { useAuthStore, type AuthUser } from '~/store/auth.store';

/** Internal staff domain — the default of workflow-service's internal-domain list. */
const INTERNAL_EMAIL_DOMAIN = '@lily.ai';

/**
 * The web's own internal rule: a super admin, or an email ending in `@lily.ai` (case-insensitive,
 * anchored, so `x@lily.ai.example.com` does not pass). Known without a request; the server may
 * say more (other internal domains, cost approvers) — see `useCostAccess`.
 */
export function isInternalUser(
  user: Pick<AuthUser, 'email' | 'isSuperAdmin'> | null | undefined,
): boolean {
  if (!user) return false;
  return user.isSuperAdmin || user.email.trim().toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN);
}

/**
 * `GET /cost-estimates/approver-status` — `{ approver, internal }` for the caller. One cached
 * query (5 min) shared with the Cost approvals nav, keyed by the signed-in user so another user
 * signing in on the same tab never reads it; never retried (a 4xx is an answer).
 */
export function useCostApproverStatus() {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  return useQuery({
    queryKey: queryKeys.costEstimates.approverStatus(userId ?? ''),
    queryFn: fetchCostApproverStatus,
    enabled: userId !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** A service's answer on who the caller is, as far as cost goes. */
export interface CostRoleAnswer {
  approver?: boolean;
  /** Absent from a service that predates it: unknown, not false. */
  internal?: boolean;
}

/**
 * The cost-visibility rule from its parts — the local rule and one service's approver-status
 * answer (undefined until it arrives). Shared by workflow-service surfaces (`useCostAccess`) and
 * catalog-service ones, which ask their own service.
 */
export function costAccessOf(
  local: boolean,
  answer: CostRoleAnswer | undefined,
  answerPending: boolean,
): CostAccess {
  const isApprover = answer?.approver === true;
  return {
    canSeeCost: local || answer?.internal === true || isApprover,
    isApprover,
    settled: local || !answerPending,
  };
}

/** Who may see cost, and whether that answer is final yet. */
export interface CostAccess {
  /** Sees cost amounts and the Cost approvals pages. */
  canSeeCost: boolean;
  /** A cost approver (workflow-service's approver list), internal or not. */
  isApprover: boolean;
  /** False only while a customer's server answer is still loading: `canSeeCost` may still
   *  turn true, so gated pages show a skeleton, never amounts, until then. */
  settled: boolean;
}

/**
 * The cost-visibility rule, matching the server: the local rule (super admin or `@lily.ai`) OR
 * approver-status says `internal` (other configured domains) or `approver`. A missing `internal`
 * (a workflow-service that predates it) is unknown and leaves the other signals to decide. A
 * failed read counts as settled on the local rule alone.
 */
export function useCostAccess(): CostAccess {
  const local = useAuthStore((s) => isInternalUser(s.user));
  const status = useCostApproverStatus();
  return costAccessOf(local, status.data, status.isPending);
}

/**
 * Whether cost display is switched on: the global switch (Platform → Cost approval, super admin)
 * AND, for lily.ai users, their own (avatar menu — nobody else has one, so it reads as on).
 * Display only — the gate, approvals and Cost approvals pages are untouched. Asked only while
 * `enabled` (viewers who may see cost); off until the answers land so a demo never flashes
 * amounts; a failed read counts as on, the default.
 */
export function useCostDisplayOn(enabled: boolean): boolean {
  const internal = useAuthStore((s) => isInternalUser(s.user));
  const global = useCostApprovalSettings({ enabled });
  const mine = useMyPreferences({ enabled: enabled && internal });
  if (!enabled) return true;
  if (global.isPending || (internal && mine.isPending)) return false;
  const personalOn = !internal || (mine.data?.showCostInUi ?? true);
  return (global.data?.showCostInUi ?? true) && personalOn;
}

/**
 * Whether the current user should see cost amounts (USD estimates, rates, spend): allowed to
 * (`useCostAccess`) and cost display switched on (`useCostDisplayOn`). Customers never do: the
 * cost gate still runs for them, Lily reviews it. The one place this rule lives on the web —
 * every cost surface reads it from here. False while an answer loads, so amounts never flash.
 * Page access (Cost approvals) stays on `useCostAccess`, which the display switches don't touch.
 */
export function useCanSeeCost(): boolean {
  const { canSeeCost } = useCostAccess();
  const displayOn = useCostDisplayOn(canSeeCost);
  return canSeeCost && displayOn;
}

/**
 * Whether to show the amounts on one payload. The cost display switches hide them first; then
 * the server's verdict wins when it gives one: workflow-service sends `amountsHidden` /
 * `costHidden` per caller (false for internal users and cost approvers, true for other
 * customers, with amounts null). Only when the flag is absent (a workflow-service that predates
 * it) does the access rule decide.
 */
export function useShowCostAmounts(hiddenByServer: boolean | null | undefined): boolean {
  const { canSeeCost } = useCostAccess();
  // A server-cleared viewer is one who may see cost, so the switches apply to them too.
  const displayOn = useCostDisplayOn(canSeeCost || hiddenByServer === false);
  if (!displayOn) return false;
  return hiddenByServer === true ? false : hiddenByServer === false ? true : canSeeCost;
}
